import { NavLink } from 'react-router';
import type { ComponentType, SVGProps } from 'react';
import { useScanQueue, useT } from '../hooks';
import { CounterIcon, HandoverIcon, QueueIcon } from './icons';

/** Number of queued scans still needing attention (waiting + rejected). */
export function useQueueBadgeCount(): number {
  const { pendingCount, items } = useScanQueue();
  const rejected = items.filter((i) => i.status === 'rejected').length;
  return pendingCount + rejected;
}

type Variant = 'bottom' | 'rail';
type NavItem = {
  to: string;
  end: boolean;
  label: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  badge?: number;
};

/**
 * Primary section navigation (Counter / Order handover / Scan queue). Rendered as a fixed
 * bottom tab bar on phones and short landscape, and as a left side rail on portrait
 * tablets (the CSS shows exactly one). A real `<nav>` landmark; the active tab carries
 * `aria-current="page"`; the Scan queue tab shows a badge with the queued-scan count.
 */
export function PrimaryNav({ variant }: { variant: Variant }) {
  const t = useT();
  const badge = useQueueBadgeCount();
  const linkClass = variant === 'bottom' ? 'bottomnav__link' : 'siderail__link';
  const iconClass = variant === 'bottom' ? 'bottomnav__icon' : 'siderail__icon';
  const labelClass = variant === 'bottom' ? 'bottomnav__label' : undefined;

  const items: NavItem[] = [
    { to: '/', end: true, label: t('operator.nav.counter'), Icon: CounterIcon },
    { to: '/handover', end: false, label: t('operator.nav.handover'), Icon: HandoverIcon },
    { to: '/queue', end: false, label: t('operator.nav.queue'), Icon: QueueIcon, badge },
  ];

  return (
    <nav aria-label={t('operator.nav.label')} className={variant === 'bottom' ? 'bottomnav' : 'siderail'} data-testid={`nav-${variant}`}>
      {items.map(({ to, end, label, Icon, badge: count }) => (
        <NavLink key={to} to={to} end={end} className={linkClass}>
          {/* The bottom bar wraps the icon in a pill element so the active tab can show a
              filled/tinted pill behind it; the side rail tints the whole link instead. */}
          {variant === 'bottom' ? (
            <span className="bottomnav__iconwrap">
              <Icon className={iconClass} />
            </span>
          ) : (
            <Icon className={iconClass} />
          )}
          <span className={labelClass}>{label}</span>
          {count !== undefined && count > 0 && (
            <span className="navbadge" data-testid="queue-badge" aria-label={t('operator.nav.queueCount', { count })}>
              {count}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
